
import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Mail, Lock, Eye, EyeOff, ArrowLeft, User, AtSign, Facebook, Chrome } from 'lucide-react'
import { Button } from '@components/ui/Button'
import { useAuthStore } from '@shared/stores/authStore'
import { authService } from '@shared/services/authService'
import toast from 'react-hot-toast'

interface SignupFormData {
  username: string
  email: string
  password: string
  confirmPassword: string
  displayName: string
}

export const SignupPage = () => {
  const [formData, setFormData] = useState<SignupFormData>({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    displayName: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [socialLoading, setSocialLoading] = useState(false)
  const [errors, setErrors] = useState<Partial<SignupFormData>>({})

  const navigate = useNavigate()
  const { signup } = useAuthStore()

  const validateForm = (): boolean => {
    const newErrors: Partial<SignupFormData> = {}

    // Username validation
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required'
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters'
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain letters, numbers, and underscores'
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    // Display name validation
    if (!formData.displayName.trim()) {
      newErrors.displayName = 'Display name is required'
    } else if (formData.displayName.length < 2) {
      newErrors.displayName = 'Display name must be at least 2 characters'
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required'
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters'
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number'
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password'
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = (field: keyof SignupFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      toast.error('Please fix the errors in the form')
      return
    }

    setLoading(true)
    try {
      const userData = {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        displayName: formData.displayName
      }

      await signup(userData)
      toast.success('Account created successfully! Welcome to Media X!')
      navigate('/')
    } catch (error: any) {
      toast.error(error.message || 'Signup failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleFacebookSignup = async () => {
    setSocialLoading(true)
    try {
      await new Promise(resolve => setTimeout(resolve, 1500))
      toast.success('Facebook signup successful!')
      navigate('/')
    } catch (error: any) {
      toast.error('Facebook signup failed: ' + error.message)
    } finally {
      setSocialLoading(false)
    }
  }

  const handleGoogleSignup = async () => {
    setSocialLoading(true)
    try {
      await new Promise(resolve => setTimeout(resolve, 1500))
      toast.success('Google signup successful!')
      navigate('/')
    } catch (error: any) {
      toast.error('Google signup failed: ' + error.message)
    } finally {
      setSocialLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <Link to="/login" className="inline-flex items-center text-white mb-4">
            <ArrowLeft size={18} className="sm:w-5 sm:h-5" />
            <span className="ml-2 text-sm sm:text-base">Back to Login</span>
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">Create Account</h1>
          <p className="text-primary-100 text-sm sm:text-base">Join Media X today and connect with friends</p>
        </div>

        {/* Signup Form */}
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
          <form onSubmit={handleSignup} className="space-y-3 sm:space-y-4">
            {/* Display Name Input */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Display Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-light" size={18} />
                <input
                  type="text"
                  value={formData.displayName}
                  onChange={(e) => handleInputChange('displayName', e.target.value)}
                  placeholder="Enter your full name"
                  className={`w-full pl-9 sm:pl-10 pr-4 py-2.5 sm:py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base ${
                    errors.displayName ? 'border-error' : 'border-border'
                  }`}
                  required
                />
              </div>
              {errors.displayName && (
                <p className="text-error text-xs sm:text-sm mt-1">{errors.displayName}</p>
              )}
            </div>

            {/* Username Input */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Username
              </label>
              <div className="relative">
                <AtSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-light" size={18} />
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  placeholder="Choose a username"
                  className={`w-full pl-9 sm:pl-10 pr-4 py-2.5 sm:py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base ${
                    errors.username ? 'border-error' : 'border-border'
                  }`}
                  required
                />
              </div>
              {errors.username && (
                <p className="text-error text-xs sm:text-sm mt-1">{errors.username}</p>
              )}
            </div>

            {/* Email Input */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-light" size={18} />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="Enter your email"
                  className={`w-full pl-9 sm:pl-10 pr-4 py-2.5 sm:py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base ${
                    errors.email ? 'border-error' : 'border-border'
                  }`}
                  required
                />
              </div>
              {errors.email && (
                <p className="text-error text-xs sm:text-sm mt-1">{errors.email}</p>
              )}
            </div>

            {/* Password Input */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-light" size={18} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder="Create a password"
                  className={`w-full pl-9 sm:pl-10 pr-10 sm:pr-12 py-2.5 sm:py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base ${
                    errors.password ? 'border-error' : 'border-border'
                  }`}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-light hover:text-text-secondary"
                >
                  {showPassword ? <EyeOff size={18} className="sm:w-5 sm:h-5" /> : <Eye size={18} className="sm:w-5 sm:h-5" />}
                </button>
              </div>
              {errors.password && (
                <p className="text-error text-xs sm:text-sm mt-1">{errors.password}</p>
              )}
            </div>

            {/* Confirm Password Input */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-light" size={18} />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  placeholder="Confirm your password"
                  className={`w-full pl-9 sm:pl-10 pr-10 sm:pr-12 py-2.5 sm:py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm sm:text-base ${
                    errors.confirmPassword ? 'border-error' : 'border-border'
                  }`}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-light hover:text-text-secondary"
                >
                  {showConfirmPassword ? <EyeOff size={18} className="sm:w-5 sm:h-5" /> : <Eye size={18} className="sm:w-5 sm:h-5" />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-error text-xs sm:text-sm mt-1">{errors.confirmPassword}</p>
              )}
            </div>

            {/* Signup Button */}
            <Button
              type="submit"
              title={loading ? 'Creating Account...' : 'Create Account'}
              variant="primary"
              className="w-full"
              disabled={loading}
            />

            {/* Divider */}
            <div className="relative my-4 sm:my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border"></div>
              </div>
              <div className="relative flex justify-center text-xs sm:text-sm">
                <span className="px-2 bg-white text-text-secondary">Or continue with</span>
              </div>
            </div>

            {/* Social Signup Buttons */}
            <div className="space-y-2 sm:space-y-3">
              <Button
                type="button"
                title={socialLoading ? 'Signing up...' : 'Continue with Facebook'}
                variant="outline"
                className="w-full"
                onClick={handleFacebookSignup}
                disabled={socialLoading}
                icon={<Facebook size={18} className="sm:w-5 sm:h-5" />}
              />
              
              <Button
                type="button"
                title={socialLoading ? 'Signing up...' : 'Continue with Google'}
                variant="outline"
                className="w-full"
                onClick={handleGoogleSignup}
                disabled={socialLoading}
                icon={<Chrome size={18} className="sm:w-5 sm:h-5" />}
              />
            </div>

            {/* Login Link */}
            <div className="text-center mt-4 sm:mt-6">
              <p className="text-xs sm:text-sm text-text-secondary">
                Already have an account?{' '}
                <Link to="/login" className="text-primary-500 hover:text-primary-600 font-medium">
                  Sign in
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
} 